import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, MessageCircle, Shield, Users, ArrowRight, Search, CheckCircle, Clock, TrendingUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LandingPage() {
  const navigate = useNavigate();
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPromoModal, setShowPromoModal] = useState(false);

  useEffect(() => {
    fetchFeaturedBusinesses();
    // Show promo modal after 2 seconds
    const timer = setTimeout(() => {
      setShowPromoModal(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const fetchFeaturedBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses?featured=true`);
      setFeaturedBusinesses(response.data.slice(0, 3)); // Show top 3 featured businesses
    } catch (error) {
      console.error('Error fetching featured businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Promotional Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit font-bold text-primary text-center">
              🎉 Limited Time Offer!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="mb-6">
              <p className="text-3xl font-outfit font-bold text-accent-orange mb-2">
                Lifetime Listing for Free
              </p>
              <p className="text-lg text-slate-600">
                Until 28 February 2026
              </p>
              <p className="text-xs text-slate-500 mt-2">
                *Terms and conditions apply
              </p>
            </div>
            <p className="text-slate-600 mb-6">
              Join The Hub today and list your business completely free! Connect with South Africa's thriving business community.
            </p>
            <Button
              onClick={() => {
                setShowPromoModal(false);
                navigate('/signup');
              }}
              className="w-full bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-6 text-lg font-medium"
              data-testid="promo-signup-button"
            >
              Sign Up Now - It's Free!
            </Button>
            <button
              onClick={() => setShowPromoModal(false)}
              className="mt-4 text-sm text-slate-500 hover:text-slate-700"
              data-testid="promo-close-button"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200" data-testid="landing-header">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/s2b-hub-logo.png" alt="The Hub by S2B Group" className="h-12 w-12 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="text-2xl font-outfit font-bold text-primary">The Hub</span>
              <span className="text-xs text-slate-600 font-normal">by S2B Group</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-primary hover:bg-primary/5"
              data-testid="login-link"
            >
              Log in
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              className="bg-accent-orange text-white hover:bg-[#D97000] rounded-full px-8 py-3 font-medium hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="signup-link"
            >
              Sign up
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32" data-testid="hero-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-outfit font-bold text-primary leading-none mb-6">
              Connect with South Africa's
              <span className="block mt-2 bg-gradient-to-r from-accent-orange to-accent-cyan bg-clip-text text-transparent">
                Thriving Business Community
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
              List your small business, showcase your expertise, and network with fellow entrepreneurs. Lifetime listing for free until 28 February 2026.*
            </p>
            <p className="text-sm text-slate-500 mb-10">
              *Terms and conditions apply
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => navigate('/signup')}
                className="bg-accent-orange text-white hover:bg-[#D97000] rounded-full px-10 py-6 text-lg font-medium hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="hero-signup-button"
              >
                List Your Business
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => navigate('/directory')}
                variant="outline"
                className="border-primary/20 text-primary hover:bg-slate-100 rounded-full px-10 py-6 text-lg font-medium"
                data-testid="browse-directory-button"
              >
                Browse Directory
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-outfit font-semibold text-primary mb-4">
              Why The Hub?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to grow your business network in South Africa
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Briefcase className="h-8 w-8 text-accent-orange" />}
              title="Business Listings"
              description="Showcase your business with detailed profiles, including industry, experience, and philosophy."
            />
            <FeatureCard
              icon={<MessageCircle className="h-8 w-8 text-accent-cyan" />}
              title="Real-time Chat"
              description="Connect instantly with other businesses through our secure messaging platform."
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8 text-accent-orange" />}
              title="Verified Legitimacy"
              description="Upload official documents to verify your business and build trust with partners."
            />
            <FeatureCard
              icon={<Users className="h-8 w-8 text-accent-cyan" />}
              title="Industry Filters"
              description="Find businesses by industry, years of experience, and location."
            />
            <FeatureCard
              icon={<Search className="h-8 w-8 text-accent-orange" />}
              title="Easy Discovery"
              description="Browse and search through categorised business listings effortlessly."
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8 text-accent-cyan" />}
              title="Safe & Moderated"
              description="Admin moderation ensures a professional and respectful environment."
            />
          </div>
        </div>
      </section>

      {/* Featured Businesses Section */}
      {!loading && featuredBusinesses.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="featured-businesses-section">
          <div className="container mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <TrendingUp className="h-8 w-8 text-accent-orange" />
                <h2 className="text-4xl md:text-5xl font-outfit font-semibold text-primary">
                  Featured Businesses
                </h2>
              </div>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Discover verified businesses trusted by our community
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBusinesses.map((business) => (
                <FeaturedBusinessCard key={business.business_id} business={business} navigate={navigate} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Button
                onClick={() => navigate('/directory')}
                variant="outline"
                className="border-primary/20 text-primary hover:bg-slate-100 rounded-full px-10 py-6 text-lg font-medium"
                data-testid="view-all-businesses-button"
              >
                View All Businesses
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-outfit font-semibold mb-6">
            Ready to Join?
          </h2>
          <p className="text-lg text-white/80 mb-2 max-w-2xl mx-auto">
            Join South Africa's growing entrepreneur community today. Lifetime listing for free until 28 February 2026.
          </p>
          <p className="text-sm text-white/60 mb-10">
            *Terms and conditions apply
          </p>
          <Button
            onClick={() => navigate('/signup')}
            className="bg-accent-orange text-white hover:bg-[#D97000] rounded-full px-10 py-6 text-lg font-medium hover:-translate-y-0.5 hover:shadow-2xl"
            data-testid="cta-signup-button"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/s2b-hub-logo.png" alt="The Hub" className="h-8 w-8 object-contain" />
            <span className="text-xl font-outfit font-bold">The Hub</span>
          </div>
          <p className="text-slate-400">
            © {new Date().getFullYear()} The Hub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md p-8 hover:-translate-y-1 group" data-testid="feature-card">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-outfit font-semibold text-primary mb-3">{title}</h3>
      <p className="text-slate-600 leading-relaxed">{description}</p>
    </div>
  );
}

function FeaturedBusinessCard({ business, navigate }) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-xl overflow-hidden hover:-translate-y-2 group cursor-pointer relative"
      onClick={() => navigate(`/business/${business.business_id}`)}
      data-testid="featured-business-card"
    >
      {/* Verified Badge */}
      <div className="absolute top-4 right-4 z-10">
        <div className="flex items-center gap-1 px-3 py-1.5 bg-accent-cyan text-white rounded-full text-xs font-medium shadow-lg">
          <CheckCircle className="h-3 w-3" />
          Verified
        </div>
      </div>

      {/* Header with gradient */}
      <div className="h-32 bg-gradient-to-br from-primary to-primary/80 relative">
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-2xl font-outfit font-bold text-white group-hover:text-accent-orange">
            {business.name}
          </h3>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-3 py-1 bg-accent-orange/10 text-accent-orange text-sm font-medium rounded-full">
            {business.industry}
          </span>
          <div className="flex items-center gap-1 text-sm text-slate-600">
            <Clock className="h-4 w-4" />
            <span>{business.years_experience} years</span>
          </div>
        </div>
        
        <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 mb-4">
          {business.philosophy}
        </p>

        {business.owner && (
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-primary font-medium">
                {business.owner.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-primary">{business.owner.name}</p>
                <p className="text-xs text-slate-500">Business Owner</p>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/business/${business.business_id}`);
          }}
          className="w-full mt-4 bg-accent-orange text-white hover:bg-[#D97000] rounded-full"
          data-testid="view-business-button"
        >
          View Profile
        </Button>
      </div>
    </div>
  );
}

export default LandingPage;
